import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BrandLockup, useBrand } from "../shell/brand";
import { useHostBrand } from "../shell/hostbrand";
import { takeSignedOut, type SignedOut as Told } from "../shell/signout";

// Restates ADR-0261 decision 6a — after a federated session, the person's own provider still holds
// them, and Zenith never ends that session.
/** What the signed-out page says after a session whose identity came through
 * the person's own provider: arpIQ's provider has ended its session, and theirs
 * has not (ADR-0261 decisions 1a and 6a). It names no single vendor, because a
 * customer's provider may be neither. */
export const SIGNED_OUT_FEDERATED = "You are signed out of this console. You may still be signed in to your own account at Google, Microsoft or your organization; sign out there too if this computer is shared.";

// Restates ADR-0261 decision 6a — a hosted, password or break-glass session has no upstream
// account to name.
/** What it says after every other session: an account hosted at arpIQ's
 * provider has no upstream account, and neither has a password or break-glass
 * session. */
export const SIGNED_OUT = "You are signed out.";

// Restates ADR-0261 decision 5a — Zenith's session is ended first, always; only the provider's is
// in doubt.
/** What it says when the provider's discovery could not be read, so the
 * browser never went there: Zenith's session is ended, and the provider's may
 * not be. */
export const SIGNED_OUT_UNREACHABLE = "You are signed out of this console. The sign-in service could not be reached, so the next sign-in on this computer may not ask for your password.";

/** The page the provider sends the browser back to after it ended its
 * session there, and the page a sign-out lands on when the provider could not
 * be reached (ADR-0261 decisions 4a and 5a). What it says comes from the
 * sign-out that sent the browser away, kept in this tab; a sign-out that began
 * on an invitation returns to it, so the invitee meets the acceptance with the
 * last person's provider session ended. Opened any other way, it says only
 * that the person is signed out. */
export default function SignedOut() {
  const navigate = useNavigate();
  const host = useHostBrand();
  const branded = host && host.source === "organization" ? host : undefined;
  useBrand(branded, "host");
  // Read once: the record is removed as it is read, and React's development
  // double render must not read it a second time and find nothing.
  const [told] = useState<Told | undefined>(() => takeSignedOut());
  const back = told?.return_to;
  useEffect(() => { if (back) navigate(back, { replace: true }); }, [back, navigate]);
  if (back) return null;
  const sentence = told?.unreachable ? SIGNED_OUT_UNREACHABLE : told?.federated ? SIGNED_OUT_FEDERATED : SIGNED_OUT;
  return (
    <main className="gate">
      <div className="gate-card form">
        {branded && <div className="gate-brand"><BrandLockup brand={branded} source="host" /></div>}
        <div>
          <h1>Signed out</h1>
          <p className="muted">{sentence}</p>
        </div>
        <Link className="btn btn-primary gate-provider" to="/">Sign in</Link>
      </div>
    </main>
  );
}
